// src/pages/UpdateOrderPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { get, put } from 'aws-amplify/api';
import {
    Heading,
    Flex,
    Button,
    TextField,
    SelectField,
    View,
} from '@aws-amplify/ui-react';

export default function UpdateOrderPage() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [errorMsg, setErrorMsg] = useState('');
    const [loading, setLoading] = useState(true);

    const [formData, setFormData] = useState({
        orderId: '',
        orderDate: '',
        OrderStatus: '',
        totalAmount: '',
        source: 'MANUAL',
        customerId: '',
        billingContact: '',
        billingAddress1: '',
        billingAddress2: '',
        billingAddress3: '',
        billingSuburb: '',
        billingState: '',
        billingCountry: '',
        price: '',
        quantity: '',
        productId: 'CUSTOM-BOX',
        Board_Grade: 'KSC',
        Box_Style: 'RSC',
        Include_Lid: 'No',
        length: '',
        width: '',
        height: '',
        _boxesPerSheet: '',
        _totalSheets: '',
        _totalLength: '',
        _totalWidth: '',
        notes: '',
        adminNotes: '',
    });

    useEffect(() => {
        const fetchOrder = async () => {
            if (!id) {
                setErrorMsg("No order ID provided for update.");
                setLoading(false);
                return;
            }
            setLoading(true);
            setErrorMsg('');
            try {
                const restOp = get({
                    apiName: 'cbx_CRUD_API',
                    path: `/unifiedorders/${encodeURIComponent(id)}`
                });
                const response = await restOp.response;
                if (!response || response.statusCode < 200 || response.statusCode > 299) {
                    throw new Error(`HTTP error ${response?.statusCode} fetching order ${id}`);
                }
                const orderData = await response.body.json();

                setFormData({
                    orderId: orderData.orderId || '',
                    orderDate: orderData.orderDate ? new Date(orderData.orderDate).toISOString().split('T')[0] : '',
                    OrderStatus: orderData.OrderStatus || '',
                    totalAmount: orderData.totalAmount?.toString() || '',
                    source: orderData.source || 'MANUAL',
                    customerId: orderData.customerId || '',
                    
                    billingContact: orderData.billingAddress?.Contact || orderData.billingAddress?.name || '',
                    billingAddress1: orderData.billingAddress?.Address1 || orderData.billingAddress?.address1 || '',
                    billingAddress2: orderData.billingAddress?.Address2 || orderData.billingAddress?.address2 || '',
                    billingAddress3: orderData.billingAddress?.Address3 || '',
                    billingSuburb: orderData.billingAddress?.Suburb || orderData.billingAddress?.city || '',
                    billingState: orderData.billingAddress?.State || orderData.billingAddress?.province || '',
                    billingCountry: orderData.billingAddress?.Country || orderData.billingAddress?.country_code || '',

                    price: orderData.items && orderData.items[0] ? orderData.items[0].price?.toString() : '',
                    quantity: orderData.items && orderData.items[0] ? orderData.items[0].quantity?.toString() : '',
                    productId: orderData.items && orderData.items[0] ? orderData.items[0].productId : 'CUSTOM-BOX',
                    
                    Board_Grade: orderData.items && orderData.items[0]?.properties ? orderData.items[0].properties.Board_Grade : 'KSC',
                    Box_Style: orderData.items && orderData.items[0]?.properties ? orderData.items[0].properties.Box_Style : 'RSC',
                    Include_Lid: orderData.items && orderData.items[0]?.properties ? orderData.items[0].properties.Include_Lid : 'No',
                    length: orderData.items && orderData.items[0]?.properties ? (orderData.items[0].properties.Length || orderData.items[0].properties.length) : '',
                    width: orderData.items && orderData.items[0]?.properties ? (orderData.items[0].properties.Width || orderData.items[0].properties.width) : '',
                    height: orderData.items && orderData.items[0]?.properties ? (orderData.items[0].properties.Height || orderData.items[0].properties.height) : '',
                    
                    _boxesPerSheet: orderData.items && orderData.items[0]?.properties ? orderData.items[0].properties._boxesPerSheet?.toString() : '',
                    _totalSheets: orderData.items && orderData.items[0]?.properties ? orderData.items[0].properties._totalSheets?.toString() : '',
                    _totalLength: orderData.items && orderData.items[0]?.properties ? orderData.items[0].properties._totalLength?.toString() : '',
                    _totalWidth: orderData.items && orderData.items[0]?.properties ? orderData.items[0].properties._totalWidth?.toString() : '',
                    notes: orderData.items && orderData.items[0]?.properties ? orderData.items[0].properties.notes : '',
                    adminNotes: orderData.adminNotes || (orderData.items && orderData.items[0]?.properties ? orderData.items[0].properties.adminNotes : ''),
                });

            } catch (error) {
                console.error('Fetch order error:', error);
                setErrorMsg(`Failed to load order: ${error.message}`);
            } finally {
                setLoading(false);
            }
        };
        fetchOrder();
    }, [id]);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!id) {
            setErrorMsg('Order ID is missing. Cannot update.');
            return;
        }

        setLoading(true);
        setErrorMsg('');

        try {
            const items = [
                {
                    price: parseFloat(formData.price) || 0,
                    quantity: parseInt(formData.quantity, 10) || 0,
                    productId: formData.productId,
                    title: formData.items && formData.items[0] ? formData.items[0].title : "Custom Box",
                    variantTitle: formData.items && formData.items[0] ? formData.items[0].variantTitle : null,
                    properties: {
                        Board_Grade: formData.Board_Grade,
                        Box_Style: formData.Box_Style,
                        Include_Lid: formData.Include_Lid,
                        Length: formData.length,
                        Width: formData.width,
                        Height: formData.height,
                        _boxesPerSheet: parseFloat(formData._boxesPerSheet) || 0,
                        _totalSheets: parseFloat(formData._totalSheets) || 0,
                        _totalLength: parseFloat(formData._totalLength) || 0,
                        _totalWidth: parseFloat(formData._totalWidth) || 0,
                        notes: formData.notes,
                        adminNotes: formData.adminNotes,
                    },
                },
            ];

            const billingAddress = {
                Contact: formData.billingContact,
                name: formData.billingContact,
                Address1: formData.billingAddress1,
                address1: formData.billingAddress1,
                Address2: formData.billingAddress2,
                address2: formData.billingAddress2,
                Address3: formData.billingAddress3,
                Suburb: formData.billingSuburb,
                city: formData.billingSuburb,
                State: formData.billingState,
                province: formData.billingState,
                Country: formData.billingCountry,
                country_code: formData.billingCountry,
                phone: formData.billingPhone || '',
                zip: formData.billingZip || '',
                company: formData.billingCompany || '',
            };
            
            const payload = {
                orderId: id,
                orderDate: formData.orderDate ? new Date(formData.orderDate).toISOString() : new Date().toISOString(),
                OrderStatus: formData.OrderStatus,
                customerId: formData.customerId,
                billingAddress,
                shippingAddress: formData.shippingAddress || billingAddress,
                totalAmount: parseFloat(formData.totalAmount) || 0,
                source: formData.source,
                items,
                currency: formData.currency || "NZD",
                fulfillmentStatus: formData.fulfillmentStatus || '',
                adminNotes: formData.adminNotes || '',
            };
            
            const restOp = put({
                apiName: 'cbx_CRUD_API',
                path: `/unifiedorders/${encodeURIComponent(id)}`,
                options: {
                    body: payload,
                },
            });
            const response = await restOp.response;

            if (response.statusCode < 200 || response.statusCode > 299) {
                const responseBody = await response.body.text();
                throw new Error(`HTTP ${response.statusCode} error updating order. Server response: ${responseBody}`);
            }

            alert('Order updated!');
            navigate('/');
        } catch (error) {
            console.error('Update error:', error);
            setErrorMsg(`Update failed: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <View padding="1rem">Loading order data...</View>;

    return (
        <div style={{ padding: '1rem' }}>
            <Heading level={3}>Update Order: {id}</Heading>
            {errorMsg && <div style={{ color: 'red', border: '1px solid red', padding: '0.5rem', marginBottom: '1rem' }}>{errorMsg}</div>}

            <form onSubmit={handleSubmit}>
                <Flex direction="column" gap="1rem" maxWidth="600px">
                    <TextField
                        label="Order ID"
                        value={formData.orderId}
                        isDisabled
                        placeholder="e.g. PO#60771"
                        required
                    />
                    <TextField
                        label="Order Date"
                        value={formData.orderDate}
                        onChange={(e) => handleChange('orderDate', e.target.value)}
                        placeholder="YYYY-MM-DD or full ISO"
                    />
                    <TextField
                        label="Order Status"
                        value={formData.OrderStatus}
                        onChange={(e) => handleChange('OrderStatus', e.target.value)}
                        placeholder="Entered / Shipped / etc."
                    />
                    <TextField
                        label="Customer ID"
                        value={formData.customerId}
                        onChange={(e) => handleChange('customerId', e.target.value)}
                    />
                    <TextField
                        label="Total Amount"
                        type="number"
                        step="any"
                        value={formData.totalAmount}
                        onChange={(e) => handleChange('totalAmount', e.target.value)}
                    />
                    <SelectField
                        label="Source"
                        value={formData.source}
                        onChange={(e) => handleChange('source', e.target.value)}
                    >
                        <option value="MANUAL">Manual</option>
                        <option value="SHOPIFY">Shopify</option>
                        <option value="PO">Purchase Order</option>
                    </SelectField>

                    <Heading level={5}>Billing Address</Heading>
                    <TextField
                        label="Contact"
                        value={formData.billingContact}
                        onChange={(e) => handleChange('billingContact', e.target.value)}
                    />
                    <TextField
                        label="Address1"
                        value={formData.billingAddress1}
                        onChange={(e) => handleChange('billingAddress1', e.target.value)}
                    />
                    <TextField
                        label="Address2"
                        value={formData.billingAddress2}
                        onChange={(e) => handleChange('billingAddress2', e.target.value)}
                    />
                    <TextField
                        label="Address3"
                        value={formData.billingAddress3}
                        onChange={(e) => handleChange('billingAddress3', e.target.value)}
                    />
                    <TextField
                        label="Suburb"
                        value={formData.billingSuburb}
                        onChange={(e) => handleChange('billingSuburb', e.target.value)}
                    />
                    <TextField
                        label="State"
                        value={formData.billingState}
                        onChange={(e) => handleChange('billingState', e.target.value)}
                    />
                    <TextField
                        label="Country"
                        value={formData.billingCountry}
                        onChange={(e) => handleChange('billingCountry', e.target.value)}
                    />

                    <Heading level={5}>Single Line Item</Heading>
                    <TextField
                        label="Product ID"
                        value={formData.productId}
                        onChange={(e) => handleChange('productId', e.target.value)}
                    />
                    <TextField
                        label="Price"
                        type="number"
                        step="any"
                        value={formData.price}
                        onChange={(e) => handleChange('price', e.target.value)}
                    />
                    <TextField
                        label="Quantity"
                        type="number"
                        step="1"
                        value={formData.quantity}
                        onChange={(e) => handleChange('quantity', e.target.value)}
                    />
                    <SelectField
                        label="Board Grade"
                        value={formData.Board_Grade}
                        onChange={(e) => handleChange('Board_Grade', e.target.value)}
                    >
                        <option value="KSC">KSC</option>
                        <option value="KTC">KTC</option>
                        <option value="WSC">WSC</option>
                    </SelectField>
                    <SelectField
                        label="Box Style"
                        value={formData.Box_Style}
                        onChange={(e) => handleChange('Box_Style', e.target.value)}
                    >
                        <option value="RSC">RSC</option>
                        <option value="Box no Lid">Box no Lid</option>
                        <option value="Box with Lid">Box with Lid</option>
                        <option value="5PF">5PF</option>
                        <option value="FOLF">FOLF</option>
                        <option value="HSC">HSC</option>
                    </SelectField>
                    <SelectField
                        label="Include Lid?"
                        value={formData.Include_Lid}
                        onChange={(e) => handleChange('Include_Lid', e.target.value)}
                    >
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                    </SelectField>

                    <TextField
                        label="Length"
                        type="number"
                        step="any"
                        value={formData.length}
                        onChange={(e) => handleChange('length', e.target.value)}
                    />
                    <TextField
                        label="Width"
                        type="number"
                        step="any"
                        value={formData.width}
                        onChange={(e) => handleChange('width', e.target.value)}
                    />
                    <TextField
                        label="Height"
                        type="number"
                        step="any"
                        value={formData.height}
                        onChange={(e) => handleChange('height', e.target.value)}
                    />

                    <Heading level={5}>Item Details (First Item)</Heading>
                    <TextField
                        label="GT Narration (Item Notes)"
                        value={formData.notes || ''}
                        onChange={(e) => handleChange('notes', e.target.value)}
                        isMultiline
                        rows={3}
                    />
                    <TextField
                        label="Admin Notes (Order Level)"
                        value={formData.adminNotes || ''}
                        onChange={(e) => handleChange('adminNotes', e.target.value)}
                        isMultiline
                        rows={3}
                    />

                    <Button type="submit" variation="primary" isLoading={loading}>
                        Update Order
                    </Button>
                </Flex>
            </form>
        </div>
    );
}
